import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDisconnect } from 'wagmi';
import CustomConnectButton from './CustomConnectButton';
import PassportConnectButton from './PassportConnectButton';

/**
 * LoginOptions Component
 *
 * This component displays the appropriate login options based on the current authentication state.
 * If the user is not logged in, it shows both AppKit and Passport login options.
 * If the user is logged in, it shows only the connected state for the active provider.
 */
export default function LoginOptions() {
  const { user, isPassportUser, logout } = useAuth();
  const { disconnect } = useDisconnect();
  const [authState, setAuthState] = useState({
    isConnected: false,
    isPassport: false
  });

  // Handle logout from Passport
  const handlePassportLogout = async () => {
    if (window.confirm('Are you sure you want to disconnect from Immutable Passport?')) {
      try {
        // Set local state first to prevent UI flicker
        setAuthState({
          isConnected: false,
          isPassport: false
        });

        // Clear any existing logout flags to ensure a clean start
        sessionStorage.removeItem('passport_logout_in_progress');
        sessionStorage.removeItem('logout_in_progress');

        // Manually clear Passport-specific localStorage items
        localStorage.removeItem('passport_user_info');
        localStorage.removeItem('passport_address');
        localStorage.removeItem('wallet_provider');

        // Perform the logout through the auth context
        await logout();

        // Clear any remaining session data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('wallet_connected');

        // Ensure the UI reflects the disconnected state
        setAuthState({
          isConnected: false,
          isPassport: false
        });

        console.log('Passport logout completed successfully');
      } catch (error) {
        console.error('Error during Passport logout:', error);

        // If logout fails, reset the auth state by checking current state
        try {
          const isPassport = isPassportUser();
          const walletProvider = localStorage.getItem('wallet_provider');
          const passportAddress = localStorage.getItem('passport_address');
          const passportUserInfo = localStorage.getItem('passport_user_info');
          const isPassportViaStorage = walletProvider === 'passport' && passportAddress && passportUserInfo;

          setAuthState({
            isConnected: !!user?.isConnected,
            isPassport: isPassport || isPassportViaStorage
          });
        } catch (stateError) {
          console.error('Error resetting auth state:', stateError);
          // As a last resort, force a page refresh
          window.location.reload();
        }
      }
    }
  };

  // Handle AppKit logout
  const handleAppKitLogout = async () => {
    try {
      console.log('LoginOptions: Initiating AppKit logout...');

      // Disconnect using wagmi
      disconnect();

      // Call the general logout function to clean up auth context
      await logout();

      // Ensure the UI reflects the disconnected state
      setAuthState({
        isConnected: false,
        isPassport: false
      });

      console.log('LoginOptions: AppKit logout completed');
    } catch (error) {
      console.error('LoginOptions: Error during AppKit logout:', error);

      // If logout fails, try to reset state manually
      try {
        setAuthState({
          isConnected: false,
          isPassport: false
        });
      } catch (stateError) {
        console.error('Error resetting auth state:', stateError);
        // As a last resort, force a page refresh
        window.location.reload();
      }
    }
  };

  // Update local state when auth state changes
  useEffect(() => {
    const updateAuthState = () => {
      // Check if user is connected via Passport
      const isPassport = isPassportUser();

      // Also check localStorage directly as a fallback
      const walletProvider = localStorage.getItem('wallet_provider');
      const passportAddress = localStorage.getItem('passport_address');
      const passportUserInfo = localStorage.getItem('passport_user_info');
      const isPassportViaStorage = walletProvider === 'passport' && passportAddress && passportUserInfo;

      // Determine if user is connected
      const isConnectedViaContext = !!user?.isConnected;
      const isConnectedViaStorage = !!localStorage.getItem('wallet_connected') || !!localStorage.getItem('token');

      // Set state based on all checks
      setAuthState({
        isConnected: isConnectedViaContext || isConnectedViaStorage,
        isPassport: isPassport || isPassportViaStorage
      });

      console.log('LoginOptions: Auth state updated', {
        isConnectedViaContext,
        isConnectedViaStorage,
        isPassport,
        isPassportViaStorage,
        user
      });
    };

    // Run the update immediately
    updateAuthState();

    // Set up a listener for the custom auth context update event
    const handleAuthContextUpdate = () => {
      console.log('LoginOptions: Received auth-context-updated event');
      updateAuthState();
    };

    // Set up a listener for passport authentication events
    const handlePassportAuthenticated = () => {
      console.log('LoginOptions: Received passport-authenticated event');
      updateAuthState();
    };

    // Set up a listener for passport user update events
    const handlePassportUserUpdated = () => {
      console.log('LoginOptions: Received passport-user-updated event');
      updateAuthState();
    };

    // Also set up a listener for storage events from other tabs
    const handleStorageChange = (event) => {
      if (event.key === 'passport_address' || event.key === 'passport_user_info' ||
          event.key === 'wallet_provider' || event.key === 'user' ||
          event.key === 'wallet_connected' || event.key === 'token') {
        console.log('LoginOptions: Storage change detected for key:', event.key);
        updateAuthState();
      }
    };

    // Set up a listener for the final auth completed event
    const handlePassportAuthCompleted = () => {
      console.log('LoginOptions: Received passport-auth-completed event');
      updateAuthState();
    };

    // Add all event listeners
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-context-updated', handleAuthContextUpdate);
    window.addEventListener('passport-authenticated', handlePassportAuthenticated);
    window.addEventListener('passport-user-updated', handlePassportUserUpdated);
    window.addEventListener('passport-auth-completed', handlePassportAuthCompleted);

    // Clean up all event listeners
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-context-updated', handleAuthContextUpdate);
      window.removeEventListener('passport-authenticated', handlePassportAuthenticated);
      window.removeEventListener('passport-user-updated', handlePassportUserUpdated);
      window.removeEventListener('passport-auth-completed', handlePassportAuthCompleted);
    };
  }, [user, isPassportUser]);

  // If user is not connected at all, show both login options
  if (!authState.isConnected) {
    return (
      <div className="space-y-3">
        <CustomConnectButton className="w-full" />
        <PassportConnectButton className="w-full" />
      </div>
    );
  }

  // If user is connected with Passport, only show Passport connected state
  if (authState.isPassport) {
    return (
      <div className="space-y-3">
        <div
          className="flex items-center justify-between p-3 rounded-lg bg-[#040F20] text-white"
          data-passport-connected="true"
        >
          <div className="flex items-center">
            <img
              src="/imx.svg"
              alt="Immutable"
              className="w-5 h-5 mr-2"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = 'none';
              }}
            />
            <span className="text-sm">Connected with Immutable</span>
          </div>
          <button
            onClick={handlePassportLogout}
            className="text-xs text-gray-400 hover:text-white transition-colors"
          >
            Disconnect
          </button>
        </div>
      </div>
    );
  }

  // Default: User is connected with AppKit, show custom connected button
  return (
    <div className="space-y-3">
      <CustomConnectButton className="w-full" />
    </div>
  );
}
